import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck, KeyRound, Smartphone, ArrowRight, Mail } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export default function Login() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [forgotOpen, setForgotOpen] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");

  useEffect(() => {
    api.get("/public-config").then(({ data }) => setAdminEmail(data.admin_email || "")).catch(() => {});
  }, []);

  const [step, setStep] = useState("credentials"); // credentials | change | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [changeToken, setChangeToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const [mfaToken, setMfaToken] = useState("");
  const [setup, setSetup] = useState(false);
  const [qr, setQr] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");

  const applyMfa = (data) => {
    setMfaToken(data.mfa_token);
    setSetup(data.setup);
    setQr(data.qr || "");
    setManualKey(data.manual_key || "");
    setStep("mfa");
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.must_change) {
        setChangeToken(data.change_token);
        setStep("change");
      } else {
        applyMfa(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const submitChange = async (e) => {
    e.preventDefault();
    if (!/^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/.test(newPw)) {
      toast.error("Password must be at least 8 characters with one capital letter and one special character");
      return;
    }
    if (newPw !== confirmPw) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/set-initial-password", { change_token: changeToken, new_password: newPw });
      toast.success("Password updated — now set up your authenticator");
      setNewPw(""); setConfirmPw("");
      applyMfa(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set password");
    } finally { setLoading(false); }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { mfa_token: mfaToken, code: code.trim() });
      completeLogin(data.token, data.user);
      toast.success("Signed in");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 scanlines">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Soverenx Systems" className="h-14 mx-auto object-contain" />
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#484F58] mt-3 font-mono-jb">Config → Design Docs · Secure Portal</div>
        </div>

        <div className="border border-white/10 bg-[#0B0C10] p-8 glow-cyan">
          {step === "credentials" ? (
            <form onSubmit={submitCreds} className="space-y-5" data-testid="login-form">
              <div className="flex items-center gap-2 mb-2">
                <KeyRound className="text-[#00F0FF]" size={18} />
                <h1 className="font-mono-jb text-xl font-bold tracking-tight text-white">Sign in</h1>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Email</label>
                <Input data-testid="login-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Password</label>
                <Input data-testid="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="" className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#00F0FF]" />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={loading}
                className="w-full rounded-sm bg-[#00F0FF] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00F0FF] h-11">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <>Continue <ArrowRight size={15} className="ml-1" /></>}
              </Button>
              <button type="button" data-testid="forgot-password-link" onClick={() => setForgotOpen(true)}
                className="w-full text-center text-[10px] font-mono-jb uppercase tracking-wider text-[#484F58] hover:text-[#8B949E] transition-colors duration-200">
                Forgot password? Contact your administrator
              </button>
            </form>
          ) : step === "change" ? (
            <form onSubmit={submitChange} className="space-y-5" data-testid="change-form">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="text-[#EAE04A]" size={18} />
                <h1 className="font-mono-jb text-xl font-bold tracking-tight text-white">Set a new password</h1>
              </div>
              <p className="text-[11px] text-[#8B949E] leading-relaxed">For security, set your own password before continuing. Enter it twice.</p>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">New Password</label>
                <Input data-testid="change-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                  className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#EAE04A]" />
                <p className="text-[10px] text-[#484F58] mt-1.5 font-mono-jb">Min 8 chars · 1 capital letter · 1 special character.</p>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Confirm Password</label>
                <Input data-testid="change-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                  className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb focus-visible:ring-[#EAE04A]" />
              </div>
              <Button data-testid="change-submit" type="submit" disabled={loading}
                className="w-full rounded-sm bg-[#EAE04A] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#EAE04A] h-11">
                {loading ? <Loader2 className="animate-spin" size={16} /> : <>Set password & continue <ArrowRight size={15} className="ml-1" /></>}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitMfa} className="space-y-5" data-testid="mfa-form">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="text-[#00FF41]" size={18} />
                <h1 className="font-mono-jb text-xl font-bold tracking-tight text-white">Two-factor</h1>
              </div>
              {setup && (
                <div className="border border-[#00F0FF]/20 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-[#00F0FF] text-xs font-mono-jb uppercase tracking-wider">
                    <Smartphone size={14} /> First-time setup
                  </div>
                  <p className="text-[11px] text-[#8B949E] leading-relaxed">Scan this QR with Microsoft Authenticator, Google Authenticator or Authy, then enter the 6-digit code.</p>
                  {qr && <img data-testid="mfa-qr" src={qr} alt="2FA QR code" className="w-44 h-44 mx-auto bg-white p-2 rounded-sm" />}
                  {manualKey && <div className="text-[10px] font-mono-jb text-[#484F58] break-all text-center">Manual key: <span className="text-[#8B949E]">{manualKey}</span></div>}
                </div>
              )}
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-[#8B949E] font-mono-jb">Authentication Code</label>
                <Input data-testid="mfa-code" inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
                  className="mt-2 bg-[#0B0C10] border-white/15 font-mono-jb text-center text-2xl tracking-[0.5em] focus-visible:ring-[#00FF41]" />
              </div>
              <Button data-testid="mfa-submit" type="submit" disabled={loading}
                className="w-full rounded-sm bg-[#00FF41] text-black font-mono-jb uppercase text-xs tracking-widest font-bold hover:bg-[#00FF41] h-11">
                {loading ? <Loader2 className="animate-spin" size={16} /> : "Verify & Sign in"}
              </Button>
            </form>
          )}
        </div>
        <div className="text-center text-[10px] text-[#484F58] font-mono-jb mt-6 tracking-wider">SOVERENX SYSTEMS · SECURE ACCESS</div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="bg-[#0B0C10] border-white/10 text-white" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono-jb uppercase tracking-wider text-[#00F0FF] flex items-center gap-2">
              <KeyRound size={16} /> Forgot password
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#8B949E] leading-relaxed py-2">
            For security, passwords can only be reset by your administrator. They'll issue you a temporary
            password, and you'll set your own on next sign in.
          </p>
          {adminEmail && (
            <a href={`mailto:${adminEmail}?subject=Password%20reset%20request%20-%20Soverenx%20PSDoc`}
              data-testid="admin-mailto"
              className="flex items-center gap-2 border border-[#00F0FF]/30 bg-[#00F0FF]/5 px-4 py-3 font-mono-jb text-sm text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors duration-200">
              <Mail size={15} /> Contact administrator: {adminEmail}
            </a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
